<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
-->

<template>
  <div
    class="ffj-monthly-grid"
    :class="{'ffj-monthly-grid--read-only': !editable}"
    data-testid="ffj-day-grid"
  >
    <div class="ffj-monthly-grid__table-wrap">
      <table
        class="ffj-monthly-grid__table"
        :aria-label="$t('forfait_jours.monthly_grid_label')"
      >
        <thead>
          <tr>
            <th
              scope="col"
              class="ffj-monthly-grid__th ffj-monthly-grid__th--day"
            >
              {{ $t('forfait_jours.day') }}
            </th>
            <th scope="col" class="ffj-monthly-grid__th">
              {{ $t('forfait_jours.day_type') }}
            </th>
            <th scope="col" class="ffj-monthly-grid__th">
              {{ $t('forfait_jours.half_day') }}
            </th>
            <th scope="col" class="ffj-monthly-grid__th">
              {{ $t('forfait_jours.rest_respected') }}
            </th>
            <th scope="col" class="ffj-monthly-grid__th">
              {{ $t('forfait_jours.comment') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="day in days"
            :key="day.day"
            :data-testid="`ffj-day-row-${day.day}`"
            :data-day="day.day"
            :class="rowClass(day)"
          >
            <td class="ffj-monthly-grid__td ffj-monthly-grid__td--day">
              <span class="ffj-monthly-grid__day-number">{{ day.day }}</span>
              <span
                v-if="day.source === 'user'"
                class="ffj-monthly-grid__badge"
                :title="$t('forfait_jours.user_modified')"
                >•</span
              >
            </td>
            <td class="ffj-monthly-grid__td">
              <day-type-dropdown
                v-if="editable && !day.nonEligible && !isNonWorking(day)"
                :model-value="day.dayTypeKey"
                :disabled="!editable"
                :options="dayTypeDropdownOptions"
                @update:model-value="onDayTypeChange(day, $event)"
              />
              <span v-else class="ffj-monthly-grid__day-type">{{
                dayTypeLabel(day)
              }}</span>
            </td>
            <td class="ffj-monthly-grid__td ffj-monthly-grid__td--center">
              <template v-if="!day.nonEligible && isHalfDayToggleable(day)">
                <input
                  type="checkbox"
                  class="ffj-monthly-grid__checkbox"
                  :data-testid="`ffj-half-day-${day.day}`"
                  :checked="day.halfDay"
                  :disabled="!editable"
                  @change="onHalfDayChange(day, $event)"
                />
              </template>
              <span v-else class="ffj-monthly-grid__muted">—</span>
            </td>
            <td class="ffj-monthly-grid__td ffj-monthly-grid__td--center">
              <template v-if="!day.nonEligible && !isNonWorking(day)">
                <input
                  type="checkbox"
                  class="ffj-monthly-grid__checkbox"
                  :data-testid="`ffj-rest-${day.day}`"
                  :checked="day.restRespected"
                  :disabled="!editable"
                  @change="onRestChange(day, $event)"
                />
              </template>
              <span v-else class="ffj-monthly-grid__muted">—</span>
            </td>
            <td class="ffj-monthly-grid__td">
              <input
                v-if="!day.nonEligible"
                type="text"
                class="ffj-monthly-grid__comment"
                :data-testid="`ffj-comment-${day.day}`"
                :value="day.comment ?? ''"
                :disabled="!editable"
                :placeholder="$t('forfait_jours.comment_placeholder')"
                @input="onCommentChange(day, $event)"
              />
              <span v-else class="ffj-monthly-grid__muted">—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Mobile card layout mirrors the table on narrow viewports. -->
    <div class="ffj-monthly-grid__cards" :aria-hidden="false">
      <article
        v-for="day in days"
        :key="`card-${day.day}`"
        class="ffj-monthly-grid__card"
        :class="rowClass(day)"
      >
        <header class="ffj-monthly-grid__card-header">
          <span class="ffj-monthly-grid__day-number">{{ day.day }}</span>
          <day-type-dropdown
            v-if="editable && !day.nonEligible && !isNonWorking(day)"
            :model-value="day.dayTypeKey"
            :disabled="!editable"
            :options="dayTypeDropdownOptions"
            @update:model-value="onDayTypeChange(day, $event)"
          />
          <span v-else class="ffj-monthly-grid__day-type">{{
            dayTypeLabel(day)
          }}</span>
          <span
            v-if="day.source === 'user'"
            class="ffj-monthly-grid__badge"
            :title="$t('forfait_jours.user_modified')"
            >•</span
          >
        </header>
        <div v-if="!day.nonEligible" class="ffj-monthly-grid__card-body">
          <label
            v-if="isHalfDayToggleable(day)"
            class="ffj-monthly-grid__field"
          >
            <span>{{ $t('forfait_jours.half_day') }}</span>
            <input
              type="checkbox"
              :data-testid="`ffj-card-half-day-${day.day}`"
              :checked="day.halfDay"
              :disabled="!editable"
              @change="onHalfDayChange(day, $event)"
            />
          </label>
          <label v-if="!isNonWorking(day)" class="ffj-monthly-grid__field">
            <span>{{ $t('forfait_jours.rest_respected') }}</span>
            <input
              type="checkbox"
              :data-testid="`ffj-card-rest-${day.day}`"
              :checked="day.restRespected"
              :disabled="!editable"
              @change="onRestChange(day, $event)"
            />
          </label>
          <label
            class="ffj-monthly-grid__field ffj-monthly-grid__field--comment"
          >
            <span>{{ $t('forfait_jours.comment') }}</span>
            <input
              type="text"
              class="ffj-monthly-grid__comment"
              :data-testid="`ffj-card-comment-${day.day}`"
              :value="day.comment ?? ''"
              :disabled="!editable"
              :placeholder="$t('forfait_jours.comment_placeholder')"
              @input="onCommentChange(day, $event)"
            />
          </label>
        </div>
        <div v-else class="ffj-monthly-grid__card-locked">
          {{ $t('forfait_jours.non_eligible_locked') }}
        </div>
      </article>
    </div>
  </div>
</template>

<script>
import DayTypeDropdown from './DayTypeDropdown.vue';
import {APIService} from '@/core/util/services/api.service';

export default {
  name: 'MonthlyGrid',
  components: {'day-type-dropdown': DayTypeDropdown},
  props: {
    days: {
      type: Array,
      required: true,
    },
    editable: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['updateDay'],
  data() {
    return {
      leaveTypes: [],
    };
  },
  computed: {
    dayTypeDropdownOptions() {
      return [
        {id: 'jour_normal', label: 'Jour normal'},
        {id: 'maladie', label: 'Maladie'},
        ...this.leaveTypes.map((item) => ({
          id: item.name,
          label: item.name,
        })),
      ];
    },
  },
  mounted() {
    // @ts-expect-error: appGlobal is not in window object by default
    if (!window.appGlobal?.baseUrl) return;
    const http = new APIService(
      // @ts-expect-error: appGlobal is not in window object by default
      window.appGlobal.baseUrl,
      '/api/v2/leave/leave-types/eligible',
    );
    http
      .getAll()
      .then(({data}) => {
        this.leaveTypes = (data?.data ?? []).filter((item) => !item.deleted);
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('Failed to load leave types for day type dropdown:', e);
      });
  },
  methods: {
    rowClass(day) {
      return {
        'ffj-day-row--user': day.source === 'user',
        'ffj-day-row--non-eligible': !!day.nonEligible,
        'ffj-day-row--non-working': !!day.nonWorking,
        'ffj-day-row--read-only': !this.editable,
      };
    },
    isNonWorking(day) {
      return (
        day.nonWorking || ['week_end', 'jour_ferie'].includes(day.dayTypeKey)
      );
    },
    isHalfDayToggleable(day) {
      if (day.nonEligible) {
        return false;
      }
      // Half-day is allowed for any active LeaveType (DECL-06),
      // including the RTT leave type.
      return ![
        'jour_normal',
        'week_end',
        'jour_ferie',
        'non_eligible',
      ].includes(day.dayTypeKey);
    },
    dayTypeLabel(day) {
      if (day.nonEligible) {
        return this.$t('forfait_jours.day_type_non_eligible');
      }
      if (day.dayTypeKey === 'week_end') {
        return this.$t('forfait_jours.day_type_week_end');
      }
      if (day.dayTypeKey === 'jour_ferie') {
        return this.$t('forfait_jours.day_type_jour_ferie');
      }
      if (day.dayTypeKey === 'jour_normal') {
        return this.$t('forfait_jours.day_type_jour_normal');
      }
      if (day.dayTypeKey === 'maladie') {
        return 'Maladie';
      }
      // Active LeaveType key — render the key as-is. Admin-driven labels
      // will be wired by a future PR (i18n catalogue).
      return day.dayTypeKey;
    },
    onDayTypeChange(day, value) {
      this.$emit('updateDay', {day: day.day, patch: {dayTypeKey: value}});
    },
    onHalfDayChange(day, event) {
      this.$emit('updateDay', {
        day: day.day,
        patch: {halfDay: event.target.checked},
      });
    },
    onRestChange(day, event) {
      this.$emit('updateDay', {
        day: day.day,
        patch: {restRespected: event.target.checked},
      });
    },
    onCommentChange(day, event) {
      this.$emit('updateDay', {
        day: day.day,
        patch: {comment: event.target.value || null},
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.ffj-monthly-grid {
  width: 100%;
  font-family: 'Nunito Sans', sans-serif;
  color: var(--oxd-interface-gray-darken-1-color, #64728c);
  &--read-only {
    pointer-events: none;
  }
  &__table-wrap {
    display: block;
    overflow-x: auto;
  }
  &__cards {
    display: none;
  }
  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;
  }
  &__th {
    text-align: left;
    padding: 0.5rem 0.75rem;
    border-bottom: 2px solid #e2e8f0;
    color: #475569;
    font-weight: 600;
    &--day {
      width: 4rem;
    }
  }
  &__td {
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
    &--day {
      font-weight: 600;
    }
    &--center {
      text-align: center;
    }
  }
  &__day-number {
    font-weight: 600;
    color: #0f172a;
  }
  &__day-type {
    color: #334155;
  }
  &__badge {
    margin-left: 0.25rem;
    color: #f59e0b;
    font-weight: 700;
  }
  &__muted {
    color: #94a3b8;
  }
  &__checkbox {
    width: 1rem;
    height: 1rem;
  }
  &__comment {
    width: 100%;
    box-sizing: border-box;
    padding: 0.25rem 0.5rem;
    border: 1px solid #cbd5e1;
    border-radius: 0.25rem;
    font-size: 0.875rem;
    background: #fff;
    &:focus {
      outline: 2px solid #2563eb;
      outline-offset: 1px;
    }
  }
}

.ffj-day-row {
  &--user {
    background: #fef3c7;
  }
  &--non-eligible {
    background: #f1f5f9;
    color: #94a3b8;
  }
}

@media (max-width: 800px) {
  .ffj-monthly-grid {
    &__table-wrap {
      display: none;
    }
    &__cards {
      display: flex;
      flex-direction: column;
      gap: 0.625rem;
    }
    &__card {
      border: 1px solid #e2e8f0;
      border-radius: 0.5rem;
      padding: 0.75rem;
      background: #fff;
    }
    &__card-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 0.75rem;
      .ffj-day-type-dropdown {
        flex: 1 1 auto;
        min-width: 0;
      }
    }
    &__day-number {
      min-width: 1.25rem;
      text-align: center;
    }
    &__card-body {
      display: flex;
      flex-direction: column;
      gap: 0.625rem;
    }
    &__field {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      font-size: 0.875rem;
      > span {
        flex: 0 0 7rem;
        color: #475569;
      }
    }
    // The comment label wraps on narrow screens, so stack it above a
    // full-width input instead of squeezing both into one row.
    &__field--comment {
      flex-direction: column;
      align-items: stretch;
      gap: 0.25rem;
      > span {
        flex: none;
      }
      .ffj-monthly-grid__comment {
        width: 100%;
      }
    }
    &__checkbox {
      width: 1.25rem;
      height: 1.25rem;
    }
    &__comment {
      padding: 0.5rem 0.75rem;
      scroll-margin-bottom: 12rem;
    }
  }
}
</style>
